import { renderOffscreen, waitForNode } from './renderOffscreen.js';
import {
  buildFontFaceCss,
  elementToCanvas,
  svgToString,
  downloadCanvasAsPng,
  downloadSvg,
} from './rasterise.js';

// Class applied to the off-screen map container so export-only style overrides
// apply to the rasterised copy but never to the on-screen map.
const EXPORT_CLASS = 'map-image-export';

// Fixed size for the exported map, so the image is the same (roomy, desktop-quality)
// layout on every breakpoint rather than the cramped, viewport-dependent on-screen
// one. Matches the map wrapper's design aspect ratio (639.84 / 1355 ≈ 2.118).
export const EXPORT_WIDTH = 1355;
export const EXPORT_HEIGHT = Math.round((EXPORT_WIDTH * 639.84) / 1355);

// The map text is set in Calibre; embed it so the raster keeps the web font.
const FONT_FAMILY = 'Calibre, sans-serif';
const FONT_PATTERN = /calibre/i;

const SCALE = 2;
const BACKGROUND = '#ffffff';

// Wait for the page's web fonts, then build the @font-face CSS to embed in the
// export so text keeps the Calibre web font. Never blocks: falls back to ''.
async function loadEmbedFontCss() {
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready.catch(() => {});
  }
  return buildFontFaceCss(FONT_PATTERN).catch(() => '');
}

// Composite the rasterised pieces onto one canvas: the readout sits in a band above
// the map, and the legend and credit are overlaid on the map's bottom corners.
function compose({ mapCanvas, readoutCanvas, legendCanvas, credit }) {
  const pad = 28 * SCALE;
  const readoutMargin = 22 * SCALE;
  const bandHeight = readoutCanvas ? readoutCanvas.height + 2 * readoutMargin : 0;

  const canvas = document.createElement('canvas');
  canvas.width = mapCanvas.width;
  canvas.height = bandHeight + mapCanvas.height;

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (readoutCanvas) {
    ctx.drawImage(readoutCanvas, pad, readoutMargin);
  }

  ctx.drawImage(mapCanvas, 0, bandHeight);

  // Legend overlaid bottom-left of the map, on a translucent backing for contrast.
  if (legendCanvas) {
    const inset = 10 * SCALE;
    const x = pad;
    const y = canvas.height - legendCanvas.height - pad;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fillRect(
      x - inset,
      y - inset,
      legendCanvas.width + 2 * inset,
      legendCanvas.height + 2 * inset
    );
    ctx.drawImage(legendCanvas, x, y);
  }

  // Credit (copyright + domain), bottom-right. Sized to match the legend's value
  // labels (the 16px small-body text).
  if (credit) {
    const size = 16 * SCALE;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';
    ctx.font = `${size}px ${FONT_FAMILY}`;
    ctx.fillStyle = '#666666';
    ctx.fillText(credit, canvas.width - pad, canvas.height - pad);
    ctx.textAlign = 'left';
  }

  return canvas;
}

// Render a fixed-size copy of the map — and, unless `furniture` is false, the readout
// and legend — off-screen, then rasterise, composite and download it as a PNG. Shared
// by the trade and policies views so the whole orchestration lives in one place.
//
// `mapVNode` is the map element to render off-screen; `svgSelector` finds its SVG once
// laid out. `readoutVNode`/`legendVNode` must each render a rasterisable root (an <svg>
// or an HTML element).
export async function exportMapImage({
  mapVNode,
  svgSelector,
  filename,
  readoutVNode,
  legendVNode,
  furniture = true,
}) {
  const map = renderOffscreen(mapVNode, {
    width: EXPORT_WIDTH,
    height: EXPORT_HEIGHT,
    className: EXPORT_CLASS,
  });

  // The readout isn't shown in the app, and the full legend is hidden below the
  // tablet breakpoint — render them off-screen so both are captured regardless of
  // viewport.
  const readout =
    furniture && readoutVNode
      ? renderOffscreen(readoutVNode, { maxWidth: EXPORT_WIDTH - 56 })
      : null;

  const legend = furniture && legendVNode ? renderOffscreen(legendVNode) : null;
  if (legend) {
    // The full legend is display:none below tablet; force it visible off-screen.
    legend.node.style.display = 'block';
  }

  try {
    const svg = await waitForNode(map.container, svgSelector);

    // Embed the fonts once and share the CSS across all three rasterisations
    // rather than rebuilding it per element.
    const fontCss = await loadEmbedFontCss();

    // Independent rasterisations — run them concurrently.
    const [mapCanvas, legendCanvas, readoutCanvas] = await Promise.all([
      elementToCanvas(svg, { scale: SCALE, fontCss, background: BACKGROUND }),
      legend ? elementToCanvas(legend.node, { scale: SCALE, fontCss }).catch(() => null) : null,
      readout ? elementToCanvas(readout.node, { scale: SCALE, fontCss }).catch(() => null) : null,
    ]);

    const credit = furniture ? `© Chatham House · ${window.location.hostname}` : null;
    const canvas = compose({ mapCanvas, readoutCanvas, legendCanvas, credit });

    await downloadCanvasAsPng(canvas, filename);
  } catch (error) {
    console.error('Failed to save map image', error);
  } finally {
    map.cleanup();
    if (readout) {
      readout.cleanup();
    }
    if (legend) {
      legend.cleanup();
    }
  }
}

// Render a fixed-size copy of the map off-screen and download it as a standalone
// SVG (styles, background image and fonts inlined). Vector and furniture-less —
// the editorial counterpart to the bare PNG export.
export async function exportMapSvg({ mapVNode, svgSelector, filename }) {
  const map = renderOffscreen(mapVNode, {
    width: EXPORT_WIDTH,
    height: EXPORT_HEIGHT,
    className: EXPORT_CLASS,
  });

  try {
    const svg = await waitForNode(map.container, svgSelector);
    const fontCss = await loadEmbedFontCss();
    const svgString = await svgToString(svg, { fontCss, background: BACKGROUND });
    downloadSvg(svgString, filename);
  } catch (error) {
    console.error('Failed to save map SVG', error);
  } finally {
    map.cleanup();
  }
}
